import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  assignDeviceTags, unassignDeviceTags,
  assignAppTags, unassignAppTags,
  assignPolicyTags, unassignPolicyTags,
} from "@/services/tagAssignmentApi";
import { getCurrentUserEmail } from "@/lib/auth";

import { MappedTagMinimal } from "@/utils/tagMapping";
type Tag = MappedTagMinimal;

export type TagResourceType =
  | "device" | "profile" | "application" | "configuration" | "patch_policy" | "user";

export const useTagAssignment = (resourceType: TagResourceType) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const email = getCurrentUserEmail() || 'unknown';

  // Resolve which API set to use based on resource type.
  // - device → device-tag API
  // - application → app-tag API
  // - profile / configuration → policy-tag API (configurations are policies)
  // - patch_policy / user → not yet supported by backend (no-op with friendly toast)
  const dispatchAssign = useCallback(async (resourceIds: string[], tagIds: string[]) => {
    const base = { created_by: email, updated_by: email };
    if (resourceType === "device") {
      await assignDeviceTags({ ...base, tag_ids: tagIds, device_ids: resourceIds });
    } else if (resourceType === "application") {
      await assignAppTags({ ...base, tag_ids: tagIds, app_ids: resourceIds });
    } else if (resourceType === "profile" || resourceType === "configuration") {
      await assignPolicyTags({ ...base, tag_ids: tagIds, policy_ids: resourceIds });
    } else {
      throw new Error(`Tag assignment for "${resourceType}" is not yet supported.`);
    }
  }, [resourceType, email]);

  const dispatchUnassign = useCallback(async (resourceId: string, tagId: string) => {
    const base = { created_by: email, updated_by: email };
    if (resourceType === "device") {
      await unassignDeviceTags({ ...base, tag_ids: [tagId], device_id: resourceId });
    } else if (resourceType === "application") {
      await unassignAppTags({ ...base, tag_ids: [tagId], app_id: resourceId });
    } else if (resourceType === "profile" || resourceType === "configuration") {
      await unassignPolicyTags({ ...base, tag_ids: [tagId], policy_id: resourceId });
    } else {
      throw new Error(`Tag removal for "${resourceType}" is not yet supported.`);
    }
  }, [resourceType, email]);

  const assignTag = useCallback(async (resourceId: string, tagId: string) => {
    setIsLoading(true);
    try {
      await dispatchAssign([resourceId], [tagId]);
      toast({ title: "Tag assigned", description: "Tag has been successfully assigned." });
      return true;
    } catch (error) {
      console.error("Error assigning tag:", error);
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to assign tag.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatchAssign, toast]);

  const removeTag = useCallback(async (resourceId: string, tagId: string) => {
    setIsLoading(true);
    try {
      await dispatchUnassign(resourceId, tagId);
      toast({ title: "Tag removed", description: "Tag has been successfully removed." });
      return true;
    } catch (error) {
      console.error("Error removing tag:", error);
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to remove tag.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatchUnassign, toast]);

  const getAssignedTags = useCallback(async (_resourceId: string): Promise<Tag[]> => {
    return [];
  }, []);

  const bulkAssignTags = useCallback(async (resourceId: string, tagIds: string[]) => {
    setIsLoading(true);
    try {
      await dispatchAssign([resourceId], tagIds);
      toast({ title: "Tags assigned", description: `${tagIds.length} tag(s) assigned.` });
      return true;
    } catch (error) {
      console.error("Error bulk assigning tags:", error);
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to assign tags.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatchAssign, toast]);

  return { assignTag, removeTag, getAssignedTags, bulkAssignTags, isLoading };
};
